import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');

interface ChatConversation {
  _id: string;
  userEmail?: string;
  userName?: string;
  userPhone?: string;
  visitorId?: string;
  visitorEmail?: string;
  visitorPhone?: string;
  status: 'active' | 'waiting' | 'closed';
  assignedAgent?: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  createdAt: string;
}

interface ChatMessage {
  _id?: string;
  conversationId: string;
  sender: 'user' | 'visitor' | 'agent';
  senderName: string;
  message: string;
  readByUser?: boolean;
  readByUserAt?: string;
  createdAt: string;
}

interface AdminChatPanelProps {
  apiBase: string;
  isSubAdmin?: boolean;
}

// Delete confirmation modal
const DeleteModal: React.FC<{
  target: ChatConversation;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
  getContactName: (c: ChatConversation) => string;
}> = ({ target, onConfirm, onCancel, isDeleting, getContactName }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 99999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)',
    animation: 'fadeIn 0.2s ease'
  }} onClick={onCancel}>
    <div onClick={e => e.stopPropagation()} style={{
      background: '#fff', borderRadius: '14px', padding: '24px 28px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.15)', maxWidth: '360px', width: '90%',
      animation: 'slideUp 0.25s ease', textAlign: 'center'
    }}>
      <div style={{
        width: '48px', height: '48px', borderRadius: '50%', background: '#fef2f2',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px'
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
          <path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
          <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
        </svg>
      </div>
      <h3 style={{ margin: '0 0 6px', fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>Delete Conversation?</h3>
      <p style={{ margin: '0 0 18px', fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5 }}>
        This will permanently delete the chat with <strong>{getContactName(target)}</strong> and remove it from the user's view as well.
      </p>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
        <button onClick={onCancel} disabled={isDeleting} style={{
          padding: '8px 20px', borderRadius: '8px', border: '1px solid #e2e8f0',
          background: '#f8fafc', color: '#475569', fontSize: '0.85rem', fontWeight: 600,
          cursor: 'pointer'
        }}>Cancel</button>
        <button onClick={onConfirm} disabled={isDeleting} style={{
          padding: '8px 20px', borderRadius: '8px', border: 'none',
          background: '#dc2626', color: '#fff', fontSize: '0.85rem', fontWeight: 600,
          cursor: isDeleting ? 'wait' : 'pointer', opacity: isDeleting ? 0.7 : 1
        }}>{isDeleting ? 'Deleting...' : 'Delete'}</button>
      </div>
    </div>
  </div>
);

const AdminChatPanel: React.FC<AdminChatPanelProps> = ({ apiBase, isSubAdmin = false }) => {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [replyText, setReplyText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ChatConversation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const url = filterStatus
        ? `${apiBase}/api/admin/chat/conversations?status=${filterStatus}`
        : `${apiBase}/api/admin/chat/conversations`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (err) {
      console.error('[AdminChat] Failed to fetch conversations:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, filterStatus]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Socket.IO connection
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('joinAdminRoom');
    });

    socket.on('conversationUpdated', (update: any) => {
      setConversations(prev => {
        const idx = prev.findIndex(c => c._id === update.conversationId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...update, _id: update.conversationId };
          return updated.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
        }
        // New conversation - refetch
        fetchConversations();
        return prev;
      });
    });

    socket.on('newMessage', (msg: ChatMessage) => {
      setMessages(prev => {
        if (prev.some(m => m._id === msg._id)) return prev;
        return [...prev, msg];
      });
    });

    socket.on('messagesReadByUser', ({ conversationId: cId, readAt }: { conversationId: string; readAt: string }) => {
      setMessages(prev =>
        prev.map(m =>
          m.conversationId === cId && m.sender === 'agent' && !m.readByUser
            ? { ...m, readByUser: true, readByUserAt: readAt }
            : m
        )
      );
    });

    socket.on('conversationDeleted', ({ conversationId }: { conversationId: string }) => {
      setConversations(prev => prev.filter(c => c._id !== conversationId));
      setSelectedConversation(prev => prev && prev._id === conversationId ? null : prev);
      if (selectedConversation?._id === conversationId) {
        setMessages([]);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [fetchConversations]);

  // Select a conversation
  const handleSelectConversation = useCallback(async (conv: ChatConversation) => {
    setSelectedConversation(conv);
    setReplyText('');

    try {
      const res = await fetch(`${apiBase}/api/admin/chat/messages/${conv._id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setMessages(data.messages);
      }
    } catch (err) {
      console.error('[AdminChat] Failed to fetch messages:', err);
    }

    // Join this conversation room for real-time updates
    if (socketRef.current) {
      socketRef.current.emit('joinConversationAsAgent', conv._id);
    }

    // Clear unread
    setConversations(prev =>
      prev.map(c => c._id === conv._id ? { ...c, unreadCount: 0 } : c)
    );
  }, [apiBase]);

  // Send reply
  const handleSendReply = useCallback(async () => {
    if (!replyText.trim() || !selectedConversation) return;
    setIsSending(true);

    try {
      const res = await fetch(`${apiBase}/api/admin/chat/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversationId: selectedConversation._id,
          message: replyText.trim()
        })
      });
      const data = await res.json();
      if (data.success) {
        setReplyText('');
      }
    } catch (err) {
      console.error('[AdminChat] Failed to send reply:', err);
    } finally {
      setIsSending(false);
    }
  }, [replyText, selectedConversation, apiBase]);

  // Delete conversation (super admin only)
  const handleDeleteConversation = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`${apiBase}/api/admin/chat/delete/${deleteTarget._id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setConversations(prev => prev.filter(c => c._id !== deleteTarget._id));
        if (selectedConversation?._id === deleteTarget._id) {
          setSelectedConversation(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error('[AdminChat] Failed to delete conversation:', err);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, selectedConversation, apiBase]);

  // Close conversation
  const handleCloseConversation = useCallback(async () => {
    if (!selectedConversation) return;
    try {
      await fetch(`${apiBase}/api/admin/chat/close/${selectedConversation._id}`, {
        method: 'POST',
        credentials: 'include'
      });
      setSelectedConversation(prev => prev ? { ...prev, status: 'closed' } : null);
      fetchConversations();
    } catch (err) {
      console.error('[AdminChat] Failed to close conversation:', err);
    }
  }, [selectedConversation, apiBase, fetchConversations]);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getContactName = (conv: ChatConversation) => {
    if (conv.userName) return conv.userName;
    if (conv.userEmail) return conv.userEmail;
    if (conv.visitorEmail) return conv.visitorEmail;
    if (conv.visitorPhone) return conv.visitorPhone;
    return conv.visitorId ? `Visitor ${conv.visitorId.substring(0, 8)}...` : 'Unknown';
  };

  const getContactDetail = (conv: ChatConversation) => {
    const parts: string[] = [];
    if (conv.userEmail) parts.push(conv.userEmail);
    if (conv.userPhone) parts.push(conv.userPhone);
    if (conv.visitorEmail) parts.push(conv.visitorEmail);
    if (conv.visitorPhone) parts.push(conv.visitorPhone);
    return parts.join(' | ') || 'No contact info';
  };

  const statusColor = (s: string) => {
    if (s === 'active') return '#22c55e';
    if (s === 'waiting') return '#f59e0b';
    return '#94a3b8';
  };

  const filteredConversations = conversations.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.userEmail?.toLowerCase() || '').includes(term) ||
      (c.userName?.toLowerCase() || '').includes(term) ||
      (c.userPhone || '').includes(term) ||
      (c.visitorEmail?.toLowerCase() || '').includes(term) ||
      (c.visitorPhone || '').includes(term) ||
      (c.lastMessage?.toLowerCase() || '').includes(term)
    );
  });

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 200px)', minHeight: '500px', background: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
      {/* Left: Conversations List */}
      <div style={{ width: '340px', minWidth: '300px', borderRight: '1px solid #e9ecef', display: 'flex', flexDirection: 'column', background: '#f8f9fb' }}>
        {/* Search + Filter */}
        <div style={{ padding: '16px', borderBottom: '1px solid #e9ecef' }}>
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '9px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.88rem', outline: 'none', marginBottom: '8px', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: '6px' }}>
            {['', 'waiting', 'active', 'closed'].map(s => (
              <button
                key={s || 'all'}
                onClick={() => setFilterStatus(s)}
                style={{
                  flex: 1, padding: '5px 0', borderRadius: '6px', border: 'none',
                  background: filterStatus === s ? '#4c6ef5' : '#e9ecef',
                  color: filterStatus === s ? '#fff' : '#64748b',
                  fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Loading...</div>
          ) : filteredConversations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.88rem' }}>No conversations found</div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv._id}
                onClick={() => handleSelectConversation(conv)}
                style={{
                  padding: '14px 16px', cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  background: selectedConversation?._id === conv._id ? '#e8ecff' : 'transparent',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={e => { if (selectedConversation?._id !== conv._id) e.currentTarget.style.background = '#f1f3f5'; }}
                onMouseLeave={e => { if (selectedConversation?._id !== conv._id) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#2d3748', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getContactName(conv)}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {conv.unreadCount > 0 && (
                      <span style={{
                        background: '#ef4444', color: '#fff', borderRadius: '10px',
                        padding: '1px 7px', fontSize: '0.7rem', fontWeight: 700
                      }}>
                        {conv.unreadCount}
                      </span>
                    )}
                    <span style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: statusColor(conv.status), flexShrink: 0
                    }} />
                  </div>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>
                  {conv.lastMessage || 'No messages yet'}
                </div>
                <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                  {conv.userEmail ? '👤 User' : '🌐 Visitor'} · {formatTime(conv.lastMessageAt)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right: Chat Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {!selectedConversation ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#94a3b8' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c4c9d4" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p style={{ marginTop: '12px', fontSize: '0.95rem' }}>Select a conversation to start replying</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid #e9ecef',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#fff', flexShrink: 0
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#2d3748' }}>
                  {getContactName(selectedConversation)}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  {getContactDetail(selectedConversation)}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  padding: '4px 10px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 600,
                  background: selectedConversation.status === 'active' ? '#dcfce7' : selectedConversation.status === 'waiting' ? '#fef3c7' : '#f1f5f9',
                  color: selectedConversation.status === 'active' ? '#166534' : selectedConversation.status === 'waiting' ? '#92400e' : '#64748b',
                  textTransform: 'capitalize'
                }}>
                  {selectedConversation.status}
                </span>
                {selectedConversation.status !== 'closed' && (
                  <button
                    onClick={handleCloseConversation}
                    style={{
                      padding: '5px 12px', borderRadius: '6px', border: '1px solid #fecaca',
                      background: '#fef2f2', color: '#dc2626', fontSize: '0.78rem', fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Close Chat
                  </button>
                )}
                {!isSubAdmin && (
                  <button
                    onClick={() => setDeleteTarget(selectedConversation)}
                    title="Delete this conversation permanently"
                    style={{
                      padding: '5px 10px', borderRadius: '6px', border: '1px solid #fecaca',
                      background: '#fef2f2', color: '#dc2626', fontSize: '0.78rem', fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div style={{
              flex: 1, overflowY: 'auto', padding: '16px 20px',
              display: 'flex', flexDirection: 'column', gap: '8px',
              background: '#f8f9fb'
            }}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem', fontSize: '0.88rem' }}>
                  No messages in this conversation yet
                </div>
              )}
              {messages.map((msg, i) => {
                const isAgent = msg.sender === 'agent';
                return (
                  <div key={msg._id || `msg-${i}`} style={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: isAgent ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        padding: '10px 14px',
                        borderRadius: isAgent ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
                        background: isAgent ? 'linear-gradient(135deg, #4c6ef5 0%, #3b5de7 100%)' : '#e8ecff',
                        color: isAgent ? '#fff' : '#2d3748',
                        fontSize: '0.88rem', lineHeight: 1.5, wordBreak: 'break-word'
                      }}>
                        {!isAgent && (
                          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#4c6ef5', marginBottom: '2px' }}>
                            {msg.senderName || (msg.sender === 'user' ? 'User' : 'Visitor')}
                          </div>
                        )}
                        {msg.message}
                      </div>
                      <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {isAgent && msg.readByUser && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', color: '#3b82f6', fontWeight: 600, fontSize: '0.62rem' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Seen
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            {selectedConversation.status !== 'closed' && (
              <div style={{
                padding: '12px 20px', borderTop: '1px solid #e9ecef',
                display: 'flex', gap: '10px', alignItems: 'center',
                background: '#fff', flexShrink: 0
              }}>
                <input
                  type="text"
                  placeholder="Type your reply..."
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
                  style={{
                    flex: 1, padding: '10px 14px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none'
                  }}
                  onFocus={e => (e.target.style.borderColor = '#4c6ef5')}
                  onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
                  autoFocus
                />
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || isSending}
                  style={{
                    padding: '10px 20px', borderRadius: '10px', border: 'none',
                    background: replyText.trim() ? 'linear-gradient(135deg, #4c6ef5 0%, #3b5de7 100%)' : '#e2e8f0',
                    color: '#fff', fontWeight: 600, fontSize: '0.88rem',
                    cursor: replyText.trim() ? 'pointer' : 'default',
                    transition: 'background 0.2s', whiteSpace: 'nowrap'
                  }}
                >
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {deleteTarget && (
        <DeleteModal
          target={deleteTarget}
          onConfirm={handleDeleteConversation}
          onCancel={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
          getContactName={getContactName}
        />
      )}
    </div>
  );
};

export default AdminChatPanel;
